import React, { useContext, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { SessionContext } from 'src/features/session/SessionContext'
import { actions as libraryPicksActions } from './LibraryPicksSlice'
import {
  getFetching,
  getHasMore,
  getLibraryPicks
} from './LibraryPicksSelector'
import Spinner from 'src/lib/ui/Spinner'
import Typography from 'src/lib/ui/Typography'
import LibraryPickRow from './components/LibraryPickRow/LibraryPickRow'
import styles from './LibraryPicks.module.sass'
import usePiwik from 'src/hooks/usePiwik'
import Button from 'src/components/Button'
import { ChevronRight } from '@mui/icons-material'

const PER_PAGE = 5

const LibraryPicks = () => {
  const { userIsLibraryAdmin = () => false, getDerivedCompany = () => null } =
    useContext(SessionContext)
  const companyId = getDerivedCompany()?.company_id
  const dispatch = useDispatch()
  const fetching = useSelector(getFetching())
  const libraryPicks = useSelector(getLibraryPicks()) || []
  const [deletingId, setDeletingId] = useState(null)
  const [page, setPage] = useState(1)
  const { handleActionTracking } = usePiwik()
  const hasMore = useSelector(getHasMore())

  useEffect(() => {
    if (companyId) {
      dispatch(
        libraryPicksActions.requestLibraryPicks({
          companyId,
          page: 1,
          per_page: PER_PAGE
        })
      )
    }
  }, [companyId])

  const handleDelete = ({ libraryPickId, articleId }) => {
    setDeletingId(libraryPickId)
    handleActionTracking('Site/Click/LibraryPicks/delete', { articleId })
    dispatch(
      libraryPicksActions.removeLibraryPick({
        libraryPickId,
        articleId,
        OnRequestSuccess: () => setDeletingId(null)
      })
    )
  }

  const handleShowMore = () => {
    const nextPage = page + 1
    setPage(nextPage)
    dispatch(
      libraryPicksActions.requestLibraryPicks({
        companyId,
        page: nextPage,
        per_page: PER_PAGE,
        append: true
      })
    )
  }

  if (fetching && page === 1) {
    return <Spinner fillColor="transparent" strokeColor="rgba(213,213,213,1)" />
  }

  return libraryPicks.length > 0 ? (
    <>
      <table className={styles.container}>
        <thead>
          <tr>
            <th>
              <Typography type="subheading">Date Flagged</Typography>
            </th>
            <th>
              <Typography type="subheading">Article</Typography>
            </th>
            <th></th>
          </tr>
        </thead>
        <tbody data-testid="LibraryPicks" className={styles.body}>
          {libraryPicks.map(pick => (
            <LibraryPickRow
              key={pick.id}
              pick={pick}
              canDelete={userIsLibraryAdmin()}
              deleting={deletingId === pick.id}
              onDelete={handleDelete}
            />
          ))}
        </tbody>
      </table>

      {hasMore && (
        <div className={styles.showMoreWrapper}>
          <Button
            label={'Show More'}
            labelColor={'#e27746'}
            labelSize={'14px'}
            variant="text"
            size="small"
            disableHover
            disableRipple
            loading={fetching}
            endIcon={<ChevronRight sx={{ color: '#e27746' }} />}
            onClick={handleShowMore}
          />
        </div>
      )}
    </>
  ) : null
}

export default LibraryPicks
